/* This code is a React component called `ManageColumn`. It is responsible for rendering a dialog box
that allows the user to manage the columns of a task board. */
import React, { useState, useCallback, useEffect } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { useQueryClient } from '@tanstack/react-query';

import { formatStageName, getTenantDetails } from 'utils/utils';
import { manageColumnData } from 'services/TaskServices';

import PriorityDropdown from 'components/UI/PriorityDropdown/PriorityDropdown';
import { ModalHeader } from 'shared/ModalHeader';
import { useToast } from 'context/ToastContext';
import { toastConstant } from 'constants/toastmessage';
import { useBoardData, useBoardActions } from '../../../context/BoardContext';
import { useManageColumnQuery } from '../../../hooks/queries';
import { queryKeys } from '../../../config/queryClient';
import Table from './Table';

const ManageColumn = (props) => {
  const { column, onHide } = props;
  const { taskStages } = useBoardData();
  const { moveCard, updateCard } = useBoardActions();
  const { addToast } = useToast();
  const queryClient = useQueryClient();

  const [dropDownSelection, setDropDownSelection] = useState({ taskStage: '', priority: '' });
  const [selectedTasks, setSelectedTasks] = useState([]);

  // Fetch tasks for this column using separate manage column query
  const infiniteQuery = useManageColumnQuery(column?.task_stage_id, {});

  // Flatten all pages into single array for the table
  const tasks = React.useMemo(() => {
    return infiniteQuery.data?.pages.flatMap((page) => page.items) || [];
  }, [infiniteQuery.data]);

  // Build stage options excluding current stage
  const taskStageOptions = React.useMemo(
    () =>
      taskStages
        ?.filter((v) => v?.task_stage_id !== column?.task_stage_id)
        ?.map((item) => ({ label: formatStageName(item?.task_stage_name), value: item?.task_stage_id })),
    [taskStages, column?.task_stage_id]
  );

  const selectedStageLabel = React.useMemo(
    () => taskStages?.find((v) => v?.task_stage_id === dropDownSelection?.taskStage)?.task_stage_name,
    [taskStages, dropDownSelection?.taskStage]
  );

  const onModalHide = () => {
    onHide();
    setDropDownSelection({ taskStage: '', priority: '' });
    setSelectedTasks([]);
  };

  // Clean up: Clear selections when modal unmounts
  useEffect(() => {
    return () => {
      setSelectedTasks([]);
      setDropDownSelection({ taskStage: '', priority: '' });
    };
  }, []);

  const handleOnSubmit = useCallback(async () => {
    if (!dropDownSelection?.taskStage && !dropDownSelection?.priority) {
      addToast(false, toastConstant.toasterType.WARNING, 'Warning', 'Please select priority or stage to move');
      return;
    }

    if (selectedTasks.length === 0) {
      addToast(false, toastConstant.toasterType.WARNING, 'Warning', 'Please select at least one task');
      return;
    }

    try {
      const [tenantId] = getTenantDetails();
      // Build the update data
      const dataToSend = {
        tasks: selectedTasks.map((item) => ({
          task_stage: dropDownSelection?.taskStage || item?.task_stage,
          priority: dropDownSelection?.priority || item?.priority,
          pk: `${tenantId}#${item?.task_id}`,
          sk: `task#${item?.task_id}`,
        })),
      };

      // Call the API
      await manageColumnData(dataToSend);
      queryClient.invalidateQueries({
        queryKey: queryKeys.tasks.all,
      });

      addToast(false, toastConstant.toasterType.SUCCESS, toastConstant.api.SUCCESS, toastConstant.message.TASK_UPDATED_SUCCESSFULLY);

      // Invalidate manage column cache to ensure fresh data on next open
      queryClient.invalidateQueries({
        queryKey: queryKeys.tasks.manage(column.task_stage_id),
      });

      setSelectedTasks([]);
      setDropDownSelection({ taskStage: '', priority: '' });
      onHide();
    } catch (error) {
      console.error('Error updating tasks:', error);
      addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.ERROR, 'Failed to update tasks');
    }
  }, [selectedTasks, dropDownSelection, column, moveCard, updateCard, addToast, onHide, queryClient]);

  const onChangeHandler = (e) => setDropDownSelection((v) => ({ ...v, [e.target.name]: e.target.value }));

  return (
    <Dialog
      className="new-task-popup mange-colum-modal datatable-responsive"
      header={ModalHeader}
      footer={<></>}
      visible={true}
      onHide={onModalHide}
    >
      <div className="d-flex justify-content-between mb-3">
        <div className="sub-title">Manage Column - {formatStageName(column?.task_stage_name)}</div>
        <div className="text-muted">
          {tasks.length} task{tasks.length !== 1 ? 's' : ''}
        </div>
      </div>
      <div className="mb-4">
        <p className="text-muted mb-0" style={{ fontSize: '14px' }}>
          Select tasks to bulk update their priority or move them to another stage
        </p>
      </div>
      <div className="d-flex mb-4">
        <div className="rounded me-3">
          <PriorityDropdown placeholder="Priority" name="priority" onChange={onChangeHandler} value={dropDownSelection?.priority} />
        </div>
        <div className="rounded">
          <Dropdown
            placeholder="Move To"
            name="taskStage"
            options={taskStageOptions}
            onChange={onChangeHandler}
            value={dropDownSelection?.taskStage}
          />
        </div>
      </div>
      <Table
        tasks={tasks}
        isLoading={infiniteQuery.isLoading}
        hasMore={infiniteQuery.hasNextPage}
        onLoadMore={infiniteQuery.fetchNextPage}
        isLoadingMore={infiniteQuery.isFetchingNextPage}
        priority={dropDownSelection.priority}
        selectedTasks={selectedTasks}
        setSelectedTasks={setSelectedTasks}
        selectedStageLabel={selectedStageLabel}
      />
      <div className="footer-btn">
        <Button onClick={onModalHide} className="bg-btn p-button-secondary outline me-1" label="Cancel" />
        <Button className="p-button-secondary" label="Save" onClick={handleOnSubmit} />
      </div>
    </Dialog>
  );
};

export default React.memo(ManageColumn);
